import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Minus } from "lucide-react";

const COLUMNS = [
  {
    id: "radio",
    name: "Radio Consultancy",
    price: "$119.99",
    bestFor: "Radio setup & app guidance",
    idealIf: "You want your radio & streaming apps set up and running",
    delivery: "1–3 days",
    consult: true,
    setup: true,
    highlight: false,
    href: "/checkout?plan=Radio%20App%20Consultancy&amount=119.99",
  },
  {
    id: "setup",
    name: "Project Setup & Kickoff",
    price: "$149.99",
    bestFor: "New website / app / software",
    idealIf: "You're starting a new product and need it built",
    delivery: "Project-based",
    consult: true,
    setup: true,
    highlight: true,
    href: "/checkout?plan=Project%20Setup%20%26%20Kickoff&amount=149.99",
  },
  {
    id: "consult",
    name: "Software Consultation",
    price: "$49.99",
    bestFor: "Quick expert advice",
    idealIf: "You just need guidance on tech, architecture or roadmap",
    delivery: "Same week",
    consult: true,
    setup: false,
    highlight: false,
    href: "/checkout?plan=Software%20Consultation&amount=49.99",
  },
  {
    id: "antivirus",
    name: "Antivirus",
    price: "from $149.99",
    bestFor: "Protecting your devices",
    idealIf: "You want genuine antivirus installed & activated",
    delivery: "Same day",
    consult: false,
    setup: true,
    highlight: false,
    href: "#antivirus",
  },
];

const ROWS = [
  { key: "bestFor", label: "Best for" },
  { key: "idealIf", label: "Ideal if…" },
  { key: "delivery", label: "Delivery" },
  { key: "oneTime", label: "One-time (no subscription)", bool: true },
  { key: "guarantee", label: "15-day free-fix guarantee", bool: true },
  { key: "consult", label: "Expert consultation", bool: true },
  { key: "setup", label: "Free setup & activation", bool: true },
];

function BoolCell({ on }) {
  return on ? (
    <Check className="h-4 w-4 text-[#3B82F6] mx-auto" />
  ) : (
    <Minus className="h-4 w-4 text-[#C6CBD9] mx-auto" />
  );
}

export default function PlanComparison() {
  const navigate = useNavigate();

  const go = (href) => {
    if (href.startsWith("#")) {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(href);
    }
  };

  const valueFor = (col, row) => {
    if (row.key === "oneTime" || row.key === "guarantee") return true;
    if (row.key === "consult") return col.consult;
    if (row.key === "setup") return col.setup;
    return col[row.key];
  };

  return (
    <section id="which-plan" className="bg-white border-y border-[#E2E8F0]" data-testid="plan-comparison-section">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#3B82F6]">Not sure where to start?</p>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight uppercase mt-3 text-[#0F172A]">Which plan is right for you?</h2>
          <p className="text-[#475569] mt-4">Compare our one-time services at a glance and pick the one that fits — every plan includes a 15-day free-fix guarantee.</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-12 overflow-x-auto rounded-2xl border border-[#E2E8F0] bg-white shadow-sm"
        >
          <table className="w-full min-w-[820px] text-sm" data-testid="plan-comparison-table">
            <thead>
              <tr>
                <th className="text-left p-5 w-56 align-bottom"></th>
                {COLUMNS.map((c) => (
                  <th key={c.id} className={`p-5 text-center align-bottom ${c.highlight ? "bg-[#3B82F6]/[0.06]" : ""}`}>
                    {c.highlight && (
                      <span className="inline-block text-[10px] font-bold uppercase tracking-wider bg-[#3B82F6] text-white px-2.5 py-1 rounded-full mb-2">Most popular</span>
                    )}
                    <div className="font-display text-base uppercase tracking-tight text-[#0F172A]">{c.name}</div>
                    <div className="font-display text-2xl font-bold text-[#3B82F6] mt-1">{c.price}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.key} className="border-t border-[#E2E8F0]">
                  <td className="p-5 font-semibold text-[#0F172A]">{row.label}</td>
                  {COLUMNS.map((c) => (
                    <td key={c.id} className={`p-5 text-center text-[#334155] ${c.highlight ? "bg-[#3B82F6]/[0.04]" : ""}`}>
                      {row.bool ? <BoolCell on={valueFor(c, row)} /> : <span>{valueFor(c, row)}</span>}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t border-[#E2E8F0]">
                <td className="p-5"></td>
                {COLUMNS.map((c) => (
                  <td key={c.id} className={`p-5 text-center ${c.highlight ? "bg-[#3B82F6]/[0.04]" : ""}`}>
                    <button
                      onClick={() => go(c.href)}
                      className={`w-full font-bold py-2.5 px-4 rounded-full transition-colors duration-300 ${c.highlight ? "bg-[#3B82F6] text-white hover:bg-[#2563EB]" : "bg-[#0F172A] text-white hover:bg-[#26395F]"}`}
                      data-testid={`compare-choose-${c.id}`}
                    >
                      Choose
                    </button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </motion.div>
      </div>
    </section>
  );
}
